import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PaymentSection from "./PaymentSection";
import { Loading } from "./common/Loading";
import { Table } from "./common/Table";
import { ChevronLeft, Pencil } from "lucide-react";
import {
  calcInvoicesFinancials,
} from "../utils/invoiceTotals";

const apiUrl =
  window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const ORDER_STATUS_BADGE = {
  completed: "bg-purple-100 text-purple-800",
  pending: "bg-yellow-100 text-yellow-800",
  cancelled: "bg-red-100 text-red-800",
  canceled: "bg-red-100 text-red-800",
  confirmed: "bg-blue-100 text-blue-800",
  delivered: "bg-green-100 text-green-800",
};

const INVOICE_STATUS_BADGE = {
  paid: "bg-green-100 text-green-800",
  draft: "bg-yellow-100 text-yellow-800",
  partially_paid: "bg-amber-100 text-amber-900",
  sent: "bg-blue-100 text-blue-800",
  overdue: "bg-red-100 text-red-800",
  cancelled: "bg-gray-200 text-gray-700",
};

function orderPaymentStatusBadge(status) {
  const s = String(status || "").toLowerCase();
  if (s === "paid") return "bg-green-100 text-green-800";
  if (s === "unpaid") return "bg-red-100 text-red-800";
  if (s === "partial") return "bg-yellow-100 text-yellow-800";
  if (s === "canceled" || s === "cancelled" || s === "refunded")
    return "bg-gray-200 text-gray-700";
  return "bg-gray-100 text-gray-800";
}

function orderStatusBadge(status) {
  const s = String(status || "").toLowerCase();
  return ORDER_STATUS_BADGE[s] || "bg-gray-100 text-gray-800";
}

const OrderDetails = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { logout, getToken } = useAuth();
  const token = getToken ? getToken() : localStorage.getItem("token");

  const [order, setOrder] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      logout();
      navigate("/login");
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (!token || !uuid) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [orderOutcome, invOutcome] = await Promise.allSettled([
          axios.get(`${apiUrl}/order/${uuid}`, {
            headers: { "x-auth-token": token },
          }),
          axios.get(`${apiUrl}/invoice/order/${uuid}`, {
            headers: { "x-auth-token": token },
          }),
        ]);

        if (orderOutcome.status !== "fulfilled") {
          const err = orderOutcome.reason;
          throw new Error(
            err?.response?.data?.message ||
              err?.response?.data?.error ||
              "Failed to load this order."
          );
        }

        const orderData = orderOutcome.value.data;
        setOrder(orderData);

        let invList = [];
        if (invOutcome.status === "fulfilled") {
          const body = invOutcome.value.data;
          if (body?.success && Array.isArray(body.data)) invList = body.data;
        }
        setInvoices(invList);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load this order.");
        setOrder(null);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [uuid, token]);

  const lineItemsSubtotal = useMemo(() => {
    if (!order?.items?.length) return 0;
    return order.items.reduce(
      (sum, line) =>
        sum +
        (parseFloat(line.quantity) || 0) * (parseFloat(line.price) || 0),
      0
    );
  }, [order]);

  const invoiceFinancials = useMemo(
    () => calcInvoicesFinancials(invoices),
    [invoices]
  );

  const displayTotal = invoiceFinancials?.total ?? lineItemsSubtotal;

  const lineRows = useMemo(
    () =>
      (order?.items || []).map((row, i) => ({
        ...row,
        __rowId: row.uuid || `${uuid}-${row.inventoryId || "x"}-${i}`,
      })),
    [order?.items, uuid]
  );

  const itemColumns = useMemo(
    () => [
      {
        key: "name",
        label: "Item",
        sortable: true,
        render: (_, row) => (
          <span className="font-medium text-gray-900">
            {row.inventory?.name || "—"}
          </span>
        ),
      },
      {
        key: "description",
        label: "Description",
        sortable: false,
        render: (value) => {
          const text = String(value || "").trim();
          if (!text) return <span className="text-gray-400">—</span>;
          return (
            <span className="whitespace-pre-wrap text-gray-700 text-sm">
              {text}
            </span>
          );
        },
      },
      {
        key: "quantity",
        label: "Qty",
        sortable: true,
      },
      {
        key: "price",
        label: "Unit price",
        sortable: true,
        render: (v) => `$${parseFloat(v || 0).toFixed(2)}`,
      },
      {
        key: "lineTotal",
        label: "Line total",
        sortable: false,
        render: (_, row) => {
          const t =
            (parseFloat(row.quantity) || 0) * (parseFloat(row.price) || 0);
          return (
            <span className="font-semibold tabular-nums">
              ${t.toFixed(2)}
            </span>
          );
        },
      },
    ],
    []
  );

  const invoiceColumns = useMemo(
    () => [
      {
        key: "invoiceNumber",
        label: "Invoice #",
        sortable: true,
        render: (value, row) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (row?.uuid) navigate(`/invoice/${row.uuid}`);
            }}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            {value ?? row.invoiceNumber ?? "—"}
          </button>
        ),
      },
      {
        key: "due_date",
        label: "Due date",
        sortable: true,
        render: (v) =>
          v ? new Date(v).toLocaleDateString() : "—",
      },
      {
        key: "total_amount",
        label: "Amount",
        sortable: true,
        render: (v) => (
          <span className="tabular-nums">${parseFloat(v || 0).toFixed(2)}</span>
        ),
      },
      {
        key: "status",
        label: "Status",
        render: (value, row) => {
          const raw = value || row.status || "";
          const s = String(raw).toLowerCase();
          const cls =
            INVOICE_STATUS_BADGE[s] || "bg-gray-100 text-gray-800";
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium capitalize ${cls}`}
            >
              {raw || "—"}
            </span>
          );
        },
      },
    ],
    [navigate]
  );

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-md rounded-md max-w-lg mx-auto text-center">
        <p className="text-red-600">{error}</p>
        <button
          type="button"
          className="btn-secondary mt-4"
          onClick={() => navigate("/orders")}
        >
          Back to orders
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-md rounded-md max-w-lg mx-auto text-center">
        <p className="text-gray-600">No order found.</p>
        <button
          type="button"
          className="btn-primary mt-4"
          onClick={() => navigate("/orders")}
        >
          Back to orders
        </button>
      </div>
    );
  }

  const customerName =
    [order.customer?.firstname, order.customer?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || "—";

  const dateRange =
    order.startDate && order.endDate
      ? `${new Date(order.startDate).toLocaleDateString()} → ${new Date(
          order.endDate
        ).toLocaleDateString()}`
      : null;

  return (
    <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-md rounded-md scroll-smooth">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/orders")}
            className="inline-flex items-center gap-0.5 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            Back to orders
          </button>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
            {order.formattedOrderNumber || "Order"}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {dateRange && <span>{dateRange}</span>}
            {dateRange && <span> · </span>}
            <span>{customerName}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Create Invoice button - only shows for confirmed/pending orders */}
          {order.status && ["confirmed", "pending"].includes(order.status.toLowerCase()) ? (
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              onClick={async () => {
                try {
                  const token = getToken ? getToken() : localStorage.getItem("token");
                  if (!token) throw new Error("Not authenticated");

                  // Call backend to create invoice from order
                  const response = await axios.post(
                    `${apiUrl}/order/${uuid}/invoices`,
                    {},
                    { headers: { "x-auth-token": token } }
                  );

                  if (response.data.success) {
                    const invoiceId = response.data.invoiceId;
                    // Navigate to the created invoice view
                    navigate(`/invoice/${invoiceId}`);
                  }
                } catch (err) {
                  console.error("Error creating invoice:", err);
                  alert(
                    err?.response?.data?.error ||
                      err.message ||
                      "Failed to create invoice from order"
                  );
                }
              }}
            >
              📄 Create Invoice
            </button>
          ) : null}

          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => navigate(`/orders/${uuid}/edit`)}
          >
            <Pencil className="w-4 h-4 shrink-0" aria-hidden />
            Edit line items
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate("/orders")}
          >
            Close
          </button>
        </div>
      </div>

      <nav
        className="mt-6 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
        aria-label="On this page"
      >
        <a
          href="#order-summary"
          className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900"
        >
          Summary
        </a>
        <span className="text-gray-300 select-none" aria-hidden>
          ·
        </span>
        <a
          href="#order-lines"
          className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900"
        >
          Line items ({lineRows.length})
        </a>
        <span className="text-gray-300 select-none" aria-hidden>
          ·
        </span>
        <a
          href="#order-invoices"
          className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900"
        >
          Invoices ({invoices.length})
        </a>
        <span className="text-gray-300 select-none" aria-hidden>
          ·
        </span>
        <a
          href="#order-payments"
          className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900"
        >
          Payments
        </a>
      </nav>

      <section id="order-summary" className="scroll-mt-24">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Summary</h2>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Order
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900 tabular-nums">
                {order.formattedOrderNumber || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Customer
              </dt>
              <dd className="mt-1 text-sm">
                {order.customer?.uuid ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/customer/${order.customer.uuid}/view`)
                    }
                    className="font-semibold text-blue-700 hover:underline text-left"
                  >
                    {customerName}
                  </button>
                ) : (
                  <span className="font-semibold text-gray-900">
                    {customerName}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Order status
              </dt>
              <dd className="mt-1">
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-medium capitalize ${orderStatusBadge(
                    order.status
                  )}`}
                >
                  {order.status || "—"}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Payment status
              </dt>
              <dd className="mt-1">
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-medium ${orderPaymentStatusBadge(
                    order.paymentStatus
                  )}`}
                >
                  {order.paymentStatus || "—"}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Start date
              </dt>
              <dd className="mt-1 text-sm font-medium text-gray-900">
                {order.startDate
                  ? new Date(order.startDate).toLocaleDateString()
                  : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                End date
              </dt>
              <dd className="mt-1 text-sm font-medium text-gray-900">
                {order.endDate
                  ? new Date(order.endDate).toLocaleDateString()
                  : "—"}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section id="order-lines" className="mt-10 scroll-mt-24">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Line items
          <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-white bg-gray-500 rounded-full align-middle">
            {lineRows.length}
          </span>
        </h2>
        <Table
          data={lineRows}
          columns={itemColumns}
          paginated
          pageSize={10}
          rowKey="__rowId"
          emptyMessage="No items on this order yet."
        />
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 max-w-md ml-auto">
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td className="text-right py-1 text-gray-500 pr-4">Line items subtotal</td>
                <td className="text-right py-1 font-medium text-gray-800 tabular-nums">
                  ${(invoiceFinancials?.subtotal ?? lineItemsSubtotal).toFixed(2)}
                </td>
              </tr>
              {invoiceFinancials && invoiceFinancials.discountAmt > 0 && (
                <tr>
                  <td className="text-right py-1 text-gray-500 pr-4">Discount</td>
                  <td className="text-right py-1 font-medium text-red-600 tabular-nums">
                    - ${invoiceFinancials.discountAmt.toFixed(2)}
                  </td>
                </tr>
              )}
              <tr className="border-t border-gray-200">
                <td className="text-right py-2 font-semibold text-gray-800 pr-4">
                  {invoiceFinancials ? "Invoice total" : "Order total"}
                </td>
                <td className="text-right py-2 text-xl font-bold text-gray-900 tabular-nums">
                  ${displayTotal.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
          {invoiceFinancials &&
            Math.abs(lineItemsSubtotal - invoiceFinancials.subtotal) > 0.01 &&
            invoiceFinancials.discountAmt <= 0 && (
              <p className="mt-2 text-xs text-gray-500 text-right">
                Line items on order: ${lineItemsSubtotal.toFixed(2)}
              </p>
            )}
        </div>
      </section>

      <section id="order-invoices" className="mt-10 scroll-mt-24">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Invoices
          <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-white bg-gray-500 rounded-full align-middle">
            {invoices.length}
          </span>
        </h2>
        <Table
          data={invoices}
          columns={invoiceColumns}
          paginated
          pageSize={10}
          rowKey="uuid"
          emptyMessage="No invoices linked to this order."
          onRowClick={(row) => row?.uuid && navigate(`/invoice/${row.uuid}`)}
        />
      </section>

      <section id="order-payments" className="mt-10 scroll-mt-24">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Payments</h2>
        {order.formattedOrderNumber && (
          <PaymentSection
            orderId={uuid}
            orderNumber={order.formattedOrderNumber}
            orderTotal={displayTotal}
          />
        )}
      </section>
    </div>
  );
};

export default OrderDetails;
